import {
  Badge,
  Box,
  Button,
  Card,
  Group,
  Loader,
  SimpleGrid,
  Stack,
  Table,
  Text,
  Title,
} from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';

import { AdminShell } from '../../components/admin-shell/AdminShell';
import { adminGet } from './_api';

type VerificationRow = {
  id: string;
  state: string;
  sla: 'ok' | 'at_risk' | 'breached';
  submitted_at?: string | null;
};
type VerificationsList = {
  items: VerificationRow[];
  total: number;
};
type DisputeRow = {
  id: string;
  short_id: string;
  category: string;
  state: string;
  opened_at: string;
  sla: 'ok' | 'at_risk' | 'breached';
};
type DisputesList = {
  items: DisputeRow[];
  total: number;
};
type Cohort = { claimed: number; cap: number; remaining: number };

export const Route = createFileRoute('/admin/')({
  component: AdminDashboard,
});

function AdminDashboard() {
  const verifications = useQuery({
    queryKey: ['admin', 'dash', 'verifications'],
    queryFn: () => adminGet<VerificationsList>('/api/admin/verifications?state=pending&limit=100'),
  });
  const disputes = useQuery({
    queryKey: ['admin', 'dash', 'disputes'],
    queryFn: () => adminGet<DisputesList>('/api/admin/disputes?state=opened&limit=100'),
  });
  const cohort = useQuery({
    queryKey: ['admin', 'dash', 'cohort'],
    queryFn: () => adminGet<Cohort>('/api/admin/cohort'),
  });

  const breachedVerifications =
    verifications.data?.items.filter((i) => i.sla === 'breached') ?? [];
  const breachedDisputes = disputes.data?.items.filter((i) => i.sla === 'breached') ?? [];
  const atRiskDisputes = disputes.data?.items.filter((i) => i.sla === 'at_risk') ?? [];
  const breachCount = breachedVerifications.length + breachedDisputes.length;
  const queueLoading = verifications.isLoading || disputes.isLoading;

  return (
    <AdminShell>
      <Stack gap="lg">
        <Title order={2}>Admin</Title>

        <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }}>
          <StatCard
            label="Pending verifications"
            value={verifications.isLoading ? '…' : String(verifications.data?.total ?? 0)}
            href="/admin/verifications"
          />
          <StatCard
            label="Open disputes"
            value={disputes.isLoading ? '…' : String(disputes.data?.total ?? 0)}
            href="/admin/disputes"
          />
          <StatCard
            label="SLA breaches"
            value={queueLoading ? '…' : String(breachCount)}
            href="/admin/disputes"
            accent={!queueLoading && breachCount > 0}
          />
          <StatCard
            label="Founding cohort"
            value={
              cohort.isLoading
                ? '…'
                : `${cohort.data?.claimed ?? 0} / ${cohort.data?.cap ?? 50}`
            }
            href="/admin/cohort"
          />
        </SimpleGrid>

        {/* SLA-breach action queue — surfaces the breached + at-risk items
            inline so the admin doesn't have to click into a sub-page just to
            see what's on fire. The breach count above is interactive; this
            section is the same data, expanded. */}
        <Card withBorder padding="md">
          <Stack gap="sm">
            <Group justify="space-between" align="center" wrap="wrap">
              <Stack gap={2}>
                <Title order={4}>SLA queue</Title>
                <Text c="dimmed" size="sm">
                  Breached items need triage now; at-risk items are within 24h of breach.
                </Text>
              </Stack>
              {breachCount > 0 && (
                <Badge color="red" variant="filled" size="lg">
                  {breachCount} breached
                </Badge>
              )}
            </Group>

            {queueLoading ? (
              <Group justify="center" py="md">
                <Loader size="sm" />
              </Group>
            ) : breachedDisputes.length === 0 &&
              breachedVerifications.length === 0 &&
              atRiskDisputes.length === 0 ? (
              <Text c="dimmed" size="sm">
                Nothing breached or at risk. Queue is clean.
              </Text>
            ) : (
              <Table.ScrollContainer minWidth={520}>
                <Table verticalSpacing="xs" highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Item</Table.Th>
                      <Table.Th>Category</Table.Th>
                      <Table.Th>Opened</Table.Th>
                      <Table.Th>SLA</Table.Th>
                      <Table.Th>State</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {breachedDisputes.map((d) => (
                      <Table.Tr key={`disp-${d.id}`}>
                        <Table.Td>
                          <Link
                            to="/admin/disputes/$id"
                            params={{ id: d.id }}
                            style={{ color: 'var(--mantine-color-brand-7)', fontWeight: 500 }}
                          >
                            Dispute {d.short_id}
                          </Link>
                        </Table.Td>
                        <Table.Td>{d.category}</Table.Td>
                        <Table.Td>{formatRelative(d.opened_at)}</Table.Td>
                        <Table.Td>
                          <Badge color="red" variant="filled" size="sm">
                            Breached
                          </Badge>
                        </Table.Td>
                        <Table.Td>{d.state}</Table.Td>
                      </Table.Tr>
                    ))}
                    {atRiskDisputes.map((d) => (
                      <Table.Tr key={`disp-${d.id}`}>
                        <Table.Td>
                          <Link
                            to="/admin/disputes/$id"
                            params={{ id: d.id }}
                            style={{ color: 'var(--mantine-color-brand-7)', fontWeight: 500 }}
                          >
                            Dispute {d.short_id}
                          </Link>
                        </Table.Td>
                        <Table.Td>{d.category}</Table.Td>
                        <Table.Td>{formatRelative(d.opened_at)}</Table.Td>
                        <Table.Td>
                          <Badge color="yellow" variant="light" size="sm">
                            At risk
                          </Badge>
                        </Table.Td>
                        <Table.Td>{d.state}</Table.Td>
                      </Table.Tr>
                    ))}
                    {breachedVerifications.map((v) => (
                      <Table.Tr key={`ver-${v.id}`}>
                        <Table.Td>
                          <Link
                            to="/admin/verifications/$id"
                            params={{ id: v.id }}
                            style={{ color: 'var(--mantine-color-brand-7)', fontWeight: 500 }}
                          >
                            Verification {v.id.slice(-6)}
                          </Link>
                        </Table.Td>
                        <Table.Td>–</Table.Td>
                        <Table.Td>
                          {v.submitted_at ? formatRelative(v.submitted_at) : '–'}
                        </Table.Td>
                        <Table.Td>
                          <Badge color="red" variant="filled" size="sm">
                            Breached
                          </Badge>
                        </Table.Td>
                        <Table.Td>{v.state}</Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            )}
          </Stack>
        </Card>

        {/* Quick-jump tiles for the rest of the admin surface. Live below the
            queue so the queue is the first thing the eye lands on. */}
        <Box>
          <Title order={4} mb="sm">
            Jump to
          </Title>
          <SimpleGrid cols={{ base: 2, sm: 3, md: 4 }} spacing="xs">
            <QuickJump label="Users" to="/admin/users" />
            <QuickJump label="Venues" to="/admin/venues" />
            <QuickJump label="Exchanges" to="/admin/exchanges" />
            <QuickJump label="Verifications" to="/admin/verifications" />
            <QuickJump label="Disputes" to="/admin/disputes" />
            <QuickJump label="Subscriptions" to="/admin/subscriptions" />
            <QuickJump label="Analytics" to="/admin/analytics" />
            <QuickJump label="Audit log" to="/admin/audit-log" />
            <QuickJump label="Sweepers" to="/admin/sweepers" />
            <QuickJump label="Cohort" to="/admin/cohort" />
            <QuickJump label="Referrals" to="/admin/referrals" />
            <QuickJump label="Announcements" to="/admin/announcements" />
          </SimpleGrid>
        </Box>
      </Stack>
    </AdminShell>
  );
}

function StatCard({
  label,
  value,
  href,
  accent = false,
}: {
  label: string;
  value: string;
  href: string;
  accent?: boolean;
}) {
  return (
    <Card
      withBorder
      padding="md"
      component={Link}
      to={href}
      style={{
        textDecoration: 'none',
        borderColor: accent ? 'var(--mantine-color-red-5)' : undefined,
        backgroundColor: accent ? 'var(--mantine-color-red-0)' : undefined,
      }}
    >
      <Stack gap={4}>
        <Text size="sm" c={accent ? 'red.7' : 'dimmed'}>
          {label}
        </Text>
        <Group gap={6} align="baseline">
          {accent ? (
            <Title order={3} c="red.7">
              {value}
            </Title>
          ) : (
            <Title order={3}>{value}</Title>
          )}
          {value === '…' ? <Loader size="xs" /> : null}
        </Group>
      </Stack>
    </Card>
  );
}

type AdminQuickJumpRoute =
  | '/admin/users'
  | '/admin/venues'
  | '/admin/exchanges'
  | '/admin/verifications'
  | '/admin/disputes'
  | '/admin/subscriptions'
  | '/admin/analytics'
  | '/admin/audit-log'
  | '/admin/sweepers'
  | '/admin/cohort'
  | '/admin/referrals'
  | '/admin/announcements';

function QuickJump({ label, to }: { label: string; to: AdminQuickJumpRoute }) {
  return (
    <Button component={Link} to={to} variant="default" fullWidth size="sm" radius="md">
      {label}
    </Button>
  );
}

/** Human-readable "5h ago" / "2d ago" / "just now" from an ISO timestamp. */
function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  const diffMs = Date.now() - then;
  if (!Number.isFinite(diffMs)) return iso;
  const m = Math.floor(diffMs / 60_000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}
